import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  Typography,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  styled,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { fetchCustomers } from "./api/customerApi";

const StatusBox = styled(Box)(({ status }) => ({
  borderRadius: "4px",
  padding: "4px 8px",
  fontWeight: "bold",
  fontSize: "0.8rem",
  display: "inline-block",
  minWidth: "70px",
  textAlign: "center",
  backgroundColor: "#2196F3",
  color: "white",
}));

const OverviewItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isFirst",
})(({ isFirst }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "70px",
  flexGrow: 1,
  textAlign: "center",
  backgroundColor: "#1b5e20",
  padding: "4px",
  borderRadius: "2px",
  color: "white",
  ...(isFirst ? {} : { borderLeft: "1px solid white" }),
}));

const CustomerOverview = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetchCustomers();
        setUsers(res);
      } catch (err) {
        setError(err.message || "Failed to fetch customers");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleRegisterClick = () => {
    history.push("/register-customer");
  };

  const totalUsers = users.length;
  const genUsers = users.filter(
    (u) => u.category?.toUpperCase() === "GENERAL"
  ).length;
  const obcUsers = users.filter(
    (u) => u.category?.toUpperCase() === "OBC"
  ).length;
  const scstUsers = users.filter((u) =>
    ["SC", "ST"].includes(u.category?.toUpperCase())
  ).length;
  const menUsers = users.filter((u) => u.gender === "Male").length;
  const womenUsers = users.filter((u) => u.gender === "Female").length;

  if (loading)
    return <Typography sx={{ p: 2 }}>Loading customers...</Typography>;
  if (error)
    return <Typography sx={{ p: 2, color: "red" }}>Error: {error}</Typography>;

  return (
    <Box>
      {/* Overview */}
      <Box
        sx={{
          backgroundColor: "#1b5e20",
          padding: "16px",
          display: "flex",
          gap: "20px",
          alignItems: "center",
          marginBottom: "20px",
          borderRadius: "4px",
        }}
      >
        <OverviewItem isFirst>
          <Typography variant="subtitle1" fontWeight="bold">
            Total Users
          </Typography>
          <Typography variant="h6">{totalUsers}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" fontWeight="bold">
            Gen
          </Typography>
          <Typography variant="h6">{genUsers}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" fontWeight="bold">
            OBC
          </Typography>
          <Typography variant="h6">{obcUsers}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" fontWeight="bold">
            SC/ST
          </Typography>
          <Typography variant="h6">{scstUsers}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" fontWeight="bold">
            Men
          </Typography>
          <Typography variant="h6">{menUsers}</Typography>
        </OverviewItem>
        <OverviewItem>
          <Typography variant="subtitle1" fontWeight="bold">
            Women
          </Typography>
          <Typography variant="h6">{womenUsers}</Typography>
        </OverviewItem>
      </Box>

      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "0 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #ccc",
            borderRadius: "4px",
            paddingLeft: "8px",
          }}
        >
          <SearchIcon color="action" />
          <TextField
            placeholder="Search here..."
            variant="standard"
            size="small"
            sx={{
              ml: 1,
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
            }}
          />
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={handleRegisterClick}
        >
          Register Customer
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: "4px", mx: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "#1b5e20" }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" />
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Farmer Name
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Gender
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Category
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Marital Status
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Mobile Number
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Aadhar Number
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Address
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Status
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "white", fontWeight: "bold" }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <TableRow key={index}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" />
                </TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.gender}</TableCell>
                <TableCell>{user.category}</TableCell>
                <TableCell>{user.maritalStatus}</TableCell>
                <TableCell>{user.contact}</TableCell>
                <TableCell>{user.aadharNumber}</TableCell>
                <TableCell>
                  {user.addressLane1}, {user.addressLane2}, {user.city}
                </TableCell>
                <TableCell>
                  <StatusBox status="In progress">In progress</StatusBox>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton size="small">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small">
                      <EditIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small">
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="More Actions">
                    <IconButton size="small">
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerOverview;
